import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DatePickerModule } from 'primeng/datepicker';
import { FieldsetModule } from 'primeng/fieldset';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { Subject, finalize, forkJoin, of, catchError, debounceTime, switchMap, map } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CrmAccount, CrmClient } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { Driver } from '../../fleet/driver/driver.model';
import { QuickFleetOnboardingDialogComponent, QuickFleetOnboardingResult } from '../../fleet/quick-fleet-onboarding/quick-fleet-onboarding-dialog.component';
import { LogisticsProjectsService } from '../../logistics-projects/logistics-projects.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { WarehousingMasterType, masterDataMap, warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import { WarehousingItem, WarehousingMasterData, WarehousingStockEntry } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';
import { itemSizeLabel as formatItemSize } from '../warehousing-item-display';

@Component({
  selector: 'app-warehousing-stock-entry-form',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, RouterModule, BreadcrumbModule, ButtonModule, CardModule, DatePickerModule, FieldsetModule,
    ConfirmDialogModule, IftaLabelModule, InputGroupModule, InputGroupAddonModule, InputNumberModule, InputTextModule, ProgressBarModule,
    PageFormSectionComponent, PageHeroComponent, PageShellComponent, QuickFleetOnboardingDialogComponent, SelectModule, TextareaModule, ToastModule
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './warehousing-stock-entry-form.component.html'
})
export class WarehousingStockEntryFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private warehousingService = inject(WarehousingService);
  private crmService = inject(CrmService);
  private destroyRef = inject(DestroyRef);
  private logisticsProjectsService = inject(LogisticsProjectsService);
  private sessionStorageService = inject(SessionStorageService);

  isLoading = false;
  isSaving = false;
  quickFleetOnboardingVisible = false;
  private readonly stockEntryId = Number(this.route.snapshot.paramMap.get('id') || 0);
  readonly isEditMode = Number.isInteger(this.stockEntryId) && this.stockEntryId > 0;
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('New Receive Stock', 'Receive Stock', '/warehousing/stock-entry');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();
  clients: CrmClient[] = [];
  accounts: CrmAccount[] = [];
  drivers: Driver[] = [];
  driversLoading = false;
  driverLoadError = false;
  private driverPage = -1;
  private driverHasMore = false;
  private driverSearch = '';
  private driverSearch$ = new Subject<{ search: string; page: number }>();
  items: WarehousingItem[] = [];
  masterData = masterDataMap();
  originalEntry: WarehousingStockEntry | null = null;

  stockEntryForm = this.fb.group({
    referenceNumber: [''],
    supplierId: [null as number | null],
    driverId: [null as number | null],
    crmClientId: [null as number | null, Validators.required],
    crmAccountId: [null as number | null, Validators.required],
    itemId: [null as number | null, Validators.required],
    regionId: [null as number | null, Validators.required],
    warehouseId: [null as number | null, Validators.required],
    zoneId: [null as number | null, Validators.required],
    conditionId: [null as number | null, Validators.required],
    skuNumber: [''],
    customerSkuNumber: [''],
    quantity: [1, [Validators.required, Validators.min(0.0001)]],
    transactionUomId: [null as number | null],
    unitSkusText: [''],
    entryDate: [this.today(), Validators.required],
    notes: [''],
    correctionReason: ['']
  });

  ngOnInit(): void {
    this.driverSearch$.pipe(
      debounceTime(250),
      switchMap(({ search, page }) => {
        this.driversLoading = true;
        this.driverLoadError = false;
        return this.warehousingService.searchReceivingDrivers(search, page, this.stockEntryForm.controls.driverId.value).pipe(
          map(result => ({ result, page, search })),
          catchError(() => {
            this.driverLoadError = true;
            return of({ result: { items: [] as Driver[], hasMore: false }, page, search });
          }),
          finalize(() => this.driversLoading = false)
        );
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ result, page, search }) => {
      if (search !== this.driverSearch) return;
      const selected = this.drivers.find(driver => driver.id === this.stockEntryForm.controls.driverId.value);
      const existing = page === 0 ? (selected ? [selected] : []) : this.drivers;
      this.drivers = [...existing, ...(result.items || []).filter(driver => !existing.some(value => value.id === driver.id))];
      this.driverPage = page;
      this.driverHasMore = !!result.hasMore;
    });
    this.stockEntryForm.get('itemId')?.valueChanges.subscribe(itemId => this.applySelectedItem(itemId));
    this.stockEntryForm.get('crmClientId')?.valueChanges.subscribe(clientId => this.reconcileAccount(clientId));
    this.stockEntryForm.get('regionId')?.valueChanges.subscribe(() => this.reconcilePlacementHierarchy());
    this.stockEntryForm.get('warehouseId')?.valueChanges.subscribe(() => this.reconcilePlacementHierarchy());
    this.loadInitialData();
  }

  loadInitialData(): void {
    this.isLoading = true;
    const companyId = this.sessionStorageService.getCompany()?.id || 0;
    forkJoin({
      clients: this.crmService.getClients(companyId, ['SUPPLIER', 'WAREHOUSE_SUPPLIER']),
      accounts: this.crmService.getAccounts(),
      items: this.warehousingService.getItems(),
      regions: this.warehousingService.getMasterData('REGION'),
      warehouses: this.warehousingService.getMasterData('WAREHOUSE'),
      zones: this.warehousingService.getMasterData('ZONE'),
      conditions: this.warehousingService.getMasterData('ITEM_CONDITION'),
      entry: this.isEditMode ? this.warehousingService.getStockEntry(this.stockEntryId) : of(null)
    }).subscribe({
      next: response => {
        this.clients = response.clients || [];
        this.accounts = response.accounts || [];
        this.items = response.items.data || [];
        this.masterData.REGION = response.regions.data || [];
        this.masterData.WAREHOUSE = this.companyWarehouses(response.warehouses.data || []);
        this.masterData.ZONE = response.zones.data || [];
        this.masterData.ITEM_CONDITION = response.conditions.data || [];
        if (this.isEditMode) {
          this.populateReceiptForEdit(response.entry?.data || null);
        }
        this.reconcilePlacementHierarchy();
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  saveStockEntry(): void {
    if (this.isSaving) {
      return;
    }
    if (this.stockEntryForm.invalid) {
      this.stockEntryForm.markAllAsTouched();
      return;
    }
    if (!this.isEditMode && this.itemRequiresUnitTracking()) {
      const unitSkus = this.unitSkus();
      if (unitSkus.length > this.unitTrackingBaseQuantity()) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Too many unit codes',
          detail: 'Supplied unit codes cannot exceed the received base quantity.'
        });
        return;
      }
    }
    if (this.isEditMode && this.hasPhysicalChanges()) {
      if (!this.inventoryCorrectionEligible()) {
        this.messageService.add({
          severity: 'error',
          summary: 'Receipt correction blocked',
          detail: this.originalEntry?.inventoryCorrectionBlockReason || 'This receipt has downstream inventory activity.'
        });
        return;
      }
      const reason = this.stockEntryForm.getRawValue().correctionReason?.trim();
      if (!reason) {
        this.stockEntryForm.controls.correctionReason.setErrors({ required: true });
        this.stockEntryForm.controls.correctionReason.markAsTouched();
        this.messageService.add({
          severity: 'warn',
          summary: 'Correction reason required',
          detail: 'Enter the reason for this inventory correction before saving.'
        });
        return;
      }
      this.confirmationService.confirm({
        header: 'Apply receipt correction?',
        message: 'The original receipt stock will be reversed and replacement stock will be booked with today\'s date. The original receipt and its ledger history will remain auditable.',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Apply correction',
        rejectLabel: 'Cancel',
        accept: () => this.applyCorrection(reason)
      });
      return;
    }
    this.saveDocumentOrNewReceipt();
  }

  hasPhysicalChanges(): boolean {
    if (!this.isEditMode || !this.originalEntry) {
      return false;
    }
    const value = this.stockEntryForm.getRawValue();
    return value.crmClientId !== this.originalEntry.crmClientId
      || value.crmAccountId !== this.originalEntry.crmAccountId
      || value.itemId !== this.originalEntry.itemId
      || value.regionId !== this.originalEntry.regionId
      || value.warehouseId !== this.originalEntry.warehouseId
      || value.zoneId !== this.originalEntry.zoneId
      || value.conditionId !== this.originalEntry.conditionId
      || value.transactionUomId !== (this.originalEntry.transactionUomId || null)
      || Number(value.quantity) !== Number(this.originalEntry.transactionUomId && this.originalEntry.transactionQuantity != null
        ? this.originalEntry.transactionQuantity : this.originalEntry.quantity);
  }

  inventoryCorrectionEligible(): boolean {
    return !!this.originalEntry?.inventoryCorrectionEligible;
  }

  corrections() {
    return this.originalEntry?.corrections || [];
  }

  accountOptions(): CrmAccount[] {
    const clientId = this.stockEntryForm.getRawValue().crmClientId;
    return clientId ? this.accounts.filter(account => account.crmClientId === clientId) : [];
  }

  private saveDocumentOrNewReceipt(): void {
    const payload = this.stockEntryPayload();
    this.isSaving = true;
    const request = this.isEditMode
      ? this.warehousingService.updateStockEntry(this.stockEntryId, payload)
      : this.warehousingService.createStockEntry(payload);
    request.pipe(finalize(() => this.isSaving = false)).subscribe(resp => {
      if (!resp.success) return;
      this.messageService.add({
        severity: 'success',
        summary: this.isEditMode ? 'Receipt Updated' : 'Stock Received',
        detail: this.isEditMode
          ? `${resp.data?.entryNumber || 'Stock receipt'} was updated.`
          : (resp.data?.entryNumber ? `${resp.data.entryNumber} was received.` : 'Stock was received.')
      });
      if (this.isEditMode) {
        this.router.navigate(['/warehousing/stock-entry']);
        return;
      }
      this.linkProjectRequirementFulfillment(resp.data?.id, resp.data?.entryNumber)
        .add(() => this.router.navigate(['/warehousing/stock-entry']));
    });
  }

  private applyCorrection(reason: string): void {
    this.isSaving = true;
    this.warehousingService.correctStockEntry(this.stockEntryId, {
      stockEntry: this.stockEntryPayload(),
      reason
    }).pipe(finalize(() => this.isSaving = false)).subscribe({
      next: response => {
        if (!response.success) {
          return;
        }
        this.messageService.add({
          severity: 'success',
          summary: 'Receipt corrected',
          detail: `${response.data?.entryNumber || 'Stock receipt'} was rebooked with an audited correction.`
        });
        this.router.navigate(['/warehousing/stock-entry']);
      },
      error: error => this.messageService.add({
        severity: 'error',
        summary: 'Receipt correction failed',
        detail: error?.error?.message || 'The receipt could not be corrected. Refresh it and try again.'
      })
    });
  }

  private stockEntryPayload(): WarehousingStockEntry {
    const value = this.stockEntryForm.getRawValue();
    return {
      referenceNumber: value.referenceNumber || undefined,
      supplierId: value.supplierId || undefined,
      driverId: value.driverId || undefined,
      crmClientId: value.crmClientId!,
      crmAccountId: value.crmAccountId!,
      itemId: value.itemId!,
      regionId: value.regionId!,
      warehouseId: value.warehouseId!,
      zoneId: value.zoneId!,
      conditionId: value.conditionId!,
      skuNumber: value.skuNumber || undefined,
      customerSkuNumber: value.customerSkuNumber || undefined,
      quantity: value.quantity!,
      transactionQuantity: value.transactionUomId ? value.quantity! : undefined,
      transactionUomId: value.transactionUomId || undefined,
      unitSkus: this.itemRequiresUnitTracking() ? this.unitSkus() : undefined,
      entryDate: value.entryDate!,
      notes: value.notes || undefined
    };
  }

  discard(): void {
    this.router.navigate(['/warehousing/stock-entry']);
  }

  options(type: WarehousingMasterType): WarehousingMasterData[] {
    return this.masterData[type] || [];
  }

  warehouseOptions(): WarehousingMasterData[] {
    const regionId = this.stockEntryForm.get('regionId')?.value as number | null;
    return regionId
      ? this.masterData.WAREHOUSE.filter(row => row.parentId === regionId)
      : [];
  }

  zoneOptions(): WarehousingMasterData[] {
    const warehouseId = this.stockEntryForm.get('warehouseId')?.value as number | null;
    return warehouseId
      ? this.masterData.ZONE.filter(row => row.parentId === warehouseId)
      : [];
  }

  isWarehouseSelectionDisabled(): boolean {
    return !this.stockEntryForm.get('regionId')?.value;
  }

  isZoneSelectionDisabled(): boolean {
    return !this.stockEntryForm.get('warehouseId')?.value;
  }

  itemOptions(): (WarehousingItem & { displayLabel: string; itemMeta: string; sizeLabel: string })[] {
    return this.items.map(item => ({
      ...item,
      displayLabel: this.itemDisplayLabel(item),
      itemMeta: this.itemMetaLabel(item),
      sizeLabel: this.itemSizeLabel(item)
    }));
  }

  selectedStockItem(): WarehousingItem | undefined {
    return this.items.find(row => row.id === this.stockEntryForm.getRawValue().itemId);
  }

  selectedItemSizeLabel(): string {
    const item = this.selectedStockItem();
    return item ? this.itemSizeLabel(item) : '';
  }

  uomOptionsForSelectedItem(): Array<{ label: string; value: number }> {
    const item = this.selectedStockItem();
    if (!item?.tenantUomId) return [];
    const baseUomId = item.baseUomId || item.tenantUomId;
    const base = [{ label: item.tenantUomName || 'Base UOM', value: baseUomId }];
    const conversions = (item.uomConversions || [])
      .filter(row => row.active !== false && row.uomId !== baseUomId)
      .map(row => ({ label: `${row.packLabel || 'UOM'} (${row.factorToBase} base)`, value: row.uomId }));
    return [...base, ...conversions];
  }

  clientName(id?: number): string {
    return this.clients.find(client => client.id === id)?.companyName || '-';
  }

  accountName(id?: number): string {
    return this.accounts.find(account => account.id === id)?.companyName || '-';
  }

  itemRequiresUnitTracking(): boolean {
    const item = this.items.find(row => row.id === this.stockEntryForm.getRawValue().itemId);
    return !!item && (item.trackingMode === 'UNIT' || item.trackingMode === 'UNIQUE_ASSET' || !!item.isDisplayStand);
  }

  unitSkus(): string[] {
    return (this.stockEntryForm.getRawValue().unitSkusText || '')
      .split(/[\n,]+/)
      .map(value => value.trim())
      .filter(Boolean);
  }

  unitTrackingBaseQuantity(): number {
    const value = this.stockEntryForm.getRawValue();
    const item = this.selectedStockItem();
    const conversion = (item?.uomConversions || []).find(row => row.uomId === value.transactionUomId && row.active !== false);
    return Number(value.quantity || 0) * Number(conversion?.factorToBase || 1);
  }

  quantityFractionDigits(): number {
    return this.itemRequiresUnitTracking() ? 0 : 4;
  }

  masterName(type: WarehousingMasterType, id?: number): string {
    return this.options(type).find(row => row.id === id)?.name || '-';
  }

  correctionRoute(snapshot?: { regionId?: number; warehouseId?: number; zoneId?: number; conditionId?: number }): string {
    if (!snapshot) {
      return '-';
    }
    return [
      this.masterName('REGION', snapshot.regionId),
      this.masterName('WAREHOUSE', snapshot.warehouseId),
      this.masterName('ZONE', snapshot.zoneId),
      this.masterName('ITEM_CONDITION', snapshot.conditionId)
    ].filter(value => value !== '-').join(' / ') || '-';
  }

  private today(): Date {
    return new Date();
  }

  private companyWarehouses(rows: WarehousingMasterData[]): WarehousingMasterData[] {
    return (rows || []).filter(row => row.warehouseOwnershipType !== 'THIRD_PARTY_VIRTUAL');
  }

  private populateReceiptForEdit(entry: WarehousingStockEntry | null): void {
    if (!entry) {
      this.messageService.add({
        severity: 'error',
        summary: 'Receipt unavailable',
        detail: 'The requested stock receipt could not be loaded.'
      });
      this.router.navigate(['/warehousing/stock-entry']);
      return;
    }
    this.originalEntry = entry;
    this.breadcrumbItems = warehousingBreadcrumb('Edit Receive Stock', 'Receive Stock', '/warehousing/stock-entry');
    this.stockEntryForm.patchValue({
      referenceNumber: entry.referenceNumber || '',
      supplierId: entry.supplierId || null,
      driverId: entry.driverId || null,
      crmClientId: entry.crmClientId || null,
      crmAccountId: entry.crmAccountId || null,
      itemId: entry.itemId,
      regionId: entry.regionId,
      warehouseId: entry.warehouseId,
      zoneId: entry.zoneId,
      conditionId: entry.conditionId,
      skuNumber: entry.skuNumber || '',
      customerSkuNumber: entry.customerSkuNumber || '',
      quantity: entry.transactionUomId && entry.transactionQuantity != null ? entry.transactionQuantity : entry.quantity,
      transactionUomId: entry.transactionUomId || null,
      unitSkusText: (entry.stockUnits || []).map(unit => unit.unitSku || unit.unitCode).filter(Boolean).join('\n'),
      entryDate: entry.entryDate ? new Date(`${String(entry.entryDate).slice(0, 10)}T00:00:00`) : this.today(),
      notes: entry.notes || ''
    }, { emitEvent: false });
    if (entry.driverId) this.loadDrivers('');
    this.updateOwnershipValidators(entry.stockOwnerType || this.selectedStockItem()?.stockOwnerType);
    const physicalControls = ['crmClientId', 'crmAccountId', 'itemId', 'regionId', 'warehouseId', 'zoneId', 'conditionId', 'quantity', 'transactionUomId'] as const;
    if (entry.inventoryCorrectionEligible) {
      physicalControls.forEach(control => this.stockEntryForm.controls[control].enable({ emitEvent: false }));
    } else {
      physicalControls.forEach(control => this.stockEntryForm.controls[control].disable({ emitEvent: false }));
    }
  }

  private applySelectedItem(itemId: number | null): void {
    const item = this.items.find(row => row.id === itemId);
    this.updateOwnershipValidators(item?.stockOwnerType);
    this.stockEntryForm.patchValue({
      crmClientId: item?.crmClientId || null,
      crmAccountId: item?.crmAccountId || null,
      skuNumber: item?.skuNumber || '',
      customerSkuNumber: item?.customerItemNumber || ''
    }, { emitEvent: false });
  }

  private updateOwnershipValidators(ownerType?: string): void {
    for (const control of [this.stockEntryForm.controls.crmClientId, this.stockEntryForm.controls.crmAccountId]) {
      if (ownerType === 'TENANT_OWNED') control.clearValidators();
      else control.setValidators(Validators.required);
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  private reconcileAccount(clientId: number | null): void {
    const accountId = this.stockEntryForm.getRawValue().crmAccountId;
    if (accountId && !this.accounts.some(account => account.id === accountId && account.crmClientId === clientId)) {
      this.stockEntryForm.patchValue({ crmAccountId: null }, { emitEvent: false });
    }
  }

  private reconcilePlacementHierarchy(): void {
    const regionId = this.stockEntryForm.get('regionId')?.value as number | null;
    const warehouseId = this.stockEntryForm.get('warehouseId')?.value as number | null;
    const zoneId = this.stockEntryForm.get('zoneId')?.value as number | null;

    const warehouseStillValid = !!warehouseId
      && this.masterData.WAREHOUSE.some(row => row.id === warehouseId && row.parentId === regionId);
    if (warehouseId && !warehouseStillValid) {
      this.stockEntryForm.patchValue({ warehouseId: null, zoneId: null }, { emitEvent: false });
      return;
    }

    const zoneStillValid = !!zoneId
      && this.masterData.ZONE.some(row => row.id === zoneId && row.parentId === warehouseId);
    if (zoneId && !zoneStillValid) {
      this.stockEntryForm.patchValue({ zoneId: null }, { emitEvent: false });
    }
  }

  private itemDisplayLabel(item: WarehousingItem): string {
    return [item.itemName, this.clientName(item.crmClientId), this.accountName(item.crmAccountId), this.itemSizeLabel(item)]
      .filter(Boolean)
      .join(' | ');
  }

  private itemMetaLabel(item: WarehousingItem): string {
    return [
      item.itemNumber || 'No item #',
      this.clientName(item.crmClientId),
      this.accountName(item.crmAccountId)
    ].join(' | ');
  }

  private itemSizeLabel(item: WarehousingItem): string {
    return formatItemSize(item);
  }

  private linkProjectRequirementFulfillment(targetResourceId?: number, sourceReference?: string) {
    const query = this.route.snapshot.queryParamMap;
    const projectId = Number(query.get('projectId') || query.get('logisticsProjectId') || 0);
    const requirementId = Number(query.get('requirementId') || 0);
    const owningCompanyId = this.sessionStorageService.getCompany()?.id || this.sessionStorageService.getCustomer()?.company?.id || 0;
    if (!projectId || !requirementId || !owningCompanyId || !targetResourceId) {
      return { add: (callback: () => void) => callback() };
    }
    const quantity = Number(query.get('quantity') || this.stockEntryForm.value.quantity || 1);
    return this.logisticsProjectsService.linkRequirementFulfillment(projectId, owningCompanyId, requirementId, {
      targetResourceType: query.get('targetResourceType') || 'WAREHOUSING_STOCK_ENTRY',
      targetResourceId,
      sourceModule: 'WAREHOUSING',
      sourceReference: sourceReference || this.stockEntryForm.value.referenceNumber || `Stock Entry #${targetResourceId}`,
      fulfilledQuantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
      fulfillmentStatus: 'CREATED',
      status: 'ACTIVE'
    }).subscribe({
      error: () => this.messageService.add({
        severity: 'warn',
        summary: 'Requirement link',
        detail: 'Stock entry was saved, but the requirement fulfillment link could not be updated.'
      })
    });
  }

  openQuickFleetOnboarding(): void {
    this.quickFleetOnboardingVisible = true;
  }

  onQuickFleetOnboarded(result: QuickFleetOnboardingResult): void {
    if (result?.driver) {
      const existing = this.drivers.find(d => d.id === result.driver.id);
      if (!existing) {
        this.drivers = [result.driver, ...this.drivers];
      }
      this.stockEntryForm.patchValue({ driverId: result.driver.id });
      this.messageService.add({
        severity: 'success',
        summary: 'Fleet Onboarded',
        detail: `Driver ${this.driverDisplayName(result.driver)} was onboarded and assigned.`
      });
    }
  }

  driverOptions(): { id: number; displayLabel: string; legalName?: string; vehiclePlate?: string }[] {
    return this.drivers.map(driver => ({
      id: driver.id!,
      displayLabel: this.driverDisplayName(driver),
      legalName: driver.legalName,
      vehiclePlate: driver.currentVehicle?.plateNumber || ''
    }));
  }

  loadDrivers(search: string): void {
    const term = search.trim();
    if (term === this.driverSearch && this.driverPage >= 0 && !this.driverLoadError) return;
    this.driverSearch = term;
    this.driverPage = 0;
    this.driverHasMore = false;
    this.driverSearch$.next({ search: term, page: 0 });
  }

  loadMoreDrivers(event: { last?: number }): void {
    if (this.driversLoading || !this.driverHasMore || (event.last ?? 0) < this.drivers.length - 4) return;
    this.driverHasMore = false;
    this.driverSearch$.next({ search: this.driverSearch, page: this.driverPage + 1 });
  }

  driverDisplayName(driver?: Driver): string {
    if (!driver) return '-';
    const name = driver.name || driver.legalName || '';
    const phone = driver.phoneNumber ? ` (${driver.phoneNumber})` : '';
    const plate = driver.currentVehicle?.plateNumber ? ` [${driver.currentVehicle.plateNumber}]` : '';
    return (name || `Driver #${driver.id}`) + phone + plate;
  }

  selectedDriverPlate(): string {
    const driverId = this.stockEntryForm.getRawValue().driverId;
    const driver = this.drivers.find(d => d.id === driverId);
    return driver?.currentVehicle?.plateNumber || '';
  }

  supplierOptions(): CrmClient[] {
    return this.clients;
  }

  supplierName(id?: number): string {
    return this.clients.find(client => client.id === id)?.companyName || '-';
  }
}
